import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent } from 'react';
import { Modal } from './Modal';
import './PhotoCropModal.css';

interface PhotoCropModalProps {
  /** The raw image the user picked; re-cropped on every open. */
  file: File;
  open: boolean;
  /** Disable the controls while the cropped result is uploading. */
  busy?: boolean;
  onCancel: () => void;
  /** Receives the square cropped JPEG, ready to upload. */
  onConfirm: (cropped: File) => void;
}

// On-screen crop area (square; the circular mask is a preview guide). The
// export is rendered at a fixed, larger size so the stored avatar stays crisp.
const VIEWPORT = 280;
const OUTPUT = 512;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

interface Offset { x: number; y: number }

/** Pick → pan/zoom inside a circle → square JPEG. Dependency-free: the visible
 *  region is drawn to an offscreen canvas and re-encoded. <PersonAvatar> renders
 *  the square result as a circle (object-fit: cover), so the mask here only has
 *  to match that framing. */
export function PhotoCropModal({ file, open, busy = false, onCancel, onConfirm }: PhotoCropModalProps): JSX.Element {
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  const [src, setSrc] = useState<string | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [coverScale, setCoverScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [loadErr, setLoadErr] = useState(false);

  // Load (and revoke) an object URL for the picked file whenever it changes.
  useEffect(() => {
    if (!open) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setNat(null);
    setLoadErr(false);
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  const clampOffset = (o: Offset, scale: number, dims: { w: number; h: number }): Offset => {
    const dispW = dims.w * scale;
    const dispH = dims.h * scale;
    return {
      x: Math.min(0, Math.max(VIEWPORT - dispW, o.x)),
      y: Math.min(0, Math.max(VIEWPORT - dispH, o.y)),
    };
  };

  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    const w = el.naturalWidth;
    const h = el.naturalHeight;
    if (!w || !h) { setLoadErr(true); return; }
    const cover = Math.max(VIEWPORT / w, VIEWPORT / h);
    setNat({ w, h });
    setCoverScale(cover);
    setZoom(1);
    setOffset({ x: (VIEWPORT - w * cover) / 2, y: (VIEWPORT - h * cover) / 2 });
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!nat) return;
    dragRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !nat) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, coverScale * zoom, nat));
  };
  const onPointerUp = () => { dragRef.current = null; };

  // Zoom about the viewport centre so the framed subject stays put.
  const applyZoom = (next: number) => {
    if (!nat) return;
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    setOffset((o) => {
      const sOld = coverScale * zoom;
      const sNew = coverScale * z;
      const cx = (VIEWPORT / 2 - o.x) / sOld;
      const cy = (VIEWPORT / 2 - o.y) / sOld;
      return clampOffset({ x: VIEWPORT / 2 - cx * sNew, y: VIEWPORT / 2 - cy * sNew }, sNew, nat);
    });
    setZoom(z);
  };

  const onWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (!nat) return;
    applyZoom(zoom + (e.deltaY < 0 ? 0.12 : -0.12));
  };

  const onUse = () => {
    const img = imgRef.current;
    if (!img || !nat) return;
    const scale = coverScale * zoom;
    const sSize = VIEWPORT / scale;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, -offset.x / scale, -offset.y / scale, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
    canvas.toBlob(
      (blob) => { if (blob) onConfirm(new File([blob], 'photo.jpg', { type: 'image/jpeg' })); },
      'image/jpeg',
      0.9,
    );
  };

  const dispW = nat ? nat.w * coverScale * zoom : 0;
  const dispH = nat ? nat.h * coverScale * zoom : 0;

  return (
    <Modal
      title="Adjust Photo"
      open={open}
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-cyan-primary" onClick={onUse} disabled={busy || !nat || loadErr}>
            {busy ? 'Saving…' : 'Use photo'}
          </button>
        </>
      }
    >
      {loadErr ? (
        <p className="crop-error" role="alert">Couldn’t read that image. Try a JPEG, PNG, GIF, or WebP.</p>
      ) : (
        <div className="crop-wrap">
          <div
            className="crop-viewport"
            style={{ width: VIEWPORT, height: VIEWPORT }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
          >
            {src && (
              <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                onError={() => setLoadErr(true)}
                className="crop-img"
                style={
                  nat
                    ? { width: dispW, height: dispH, transform: `translate(${offset.x}px, ${offset.y}px)` }
                    : { display: 'none' }
                }
              />
            )}
            <div className="crop-mask" aria-hidden="true" />
          </div>
          <div className="crop-zoom-row">
            <button type="button" className="crop-zoom-btn" onClick={() => applyZoom(zoom - 0.2)} disabled={!nat} aria-label="Zoom out">−</button>
            <input
              type="range"
              className="crop-zoom-slider"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.01}
              value={zoom}
              onChange={(e) => applyZoom(Number(e.target.value))}
              disabled={!nat}
              aria-label="Zoom"
            />
            <button type="button" className="crop-zoom-btn" onClick={() => applyZoom(zoom + 0.2)} disabled={!nat} aria-label="Zoom in">+</button>
          </div>
          <p className="crop-hint">Drag to reposition · scroll or use the slider to zoom</p>
        </div>
      )}
    </Modal>
  );
}
